import styled from "styled-components";

// ─── Layout ──────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr 480px;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr 400px;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

export const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 25px;
    overflow-y: auto;
`;

export const RightSection = styled.div<{ isVisible: boolean }>`
    display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
    flex-direction: column;
    align-items: center;
    padding: 20px;
    background-color: #f5f5f5;
    border-radius: 12px;
    transition: all 0.3s ease;

    @media (max-width: 992px) {
        padding: 15px;
        position: fixed;
        top: 0;
        right: ${({ isVisible }) => (isVisible ? "0" : "-100%")};
        width: 100%;
        max-width: 450px;
        height: 100vh;
        z-index: 999;
        overflow-y: auto;
        box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
        display: flex;
    }

    @media (max-width: 576px) {
        padding: 10px;
        max-width: 100%;
    }
`;

export const Card = styled.div`
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
`;

export const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

export const SectionTitle = styled.h5`
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0;
`;

export const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const ToggleLabel = styled.span`
    font-size: 14px;
    color: #666;
`;

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
`;

export const SmallToggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 40px;
    height: 22px;
    border-radius: 11px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "20px" : "2px")};
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const LanguageTabs = styled.div`
    display: flex;
    gap: 5px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (max-width: 576px) {
        gap: 8px;
        justify-content: center;
    }
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 6px 16px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    border-radius: 20px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#fff"};
    color: ${({ isActive }) => (isActive ? "#fff" : "#333")};
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

export const InputRow = styled.div`
    display: grid;
    grid-template-columns: 100px 1fr 2fr;
    gap: 10px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    @media (max-width: 768px) {
        grid-template-columns: 80px 1fr 1.5fr;
        gap: 8px;
        padding: 8px 0;
    }

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 15px 0;
    }
`;

export const InputLabel = styled.span`
    font-size: 13px;
    color: #666;
`;

export const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    width: 100%;

    &:focus {
        outline: none;
        border-color: #999;
    }
`;

export const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ResetButton = styled.button<{ mainColor: string }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ mainColor }) => mainColor};
    transition: background-color 0.2s ease;
    border-radius: 4px;
    flex-shrink: 0;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const ResetAllButton = styled.button<{ mainColor: string }>`
    padding: 6px 12px;
    background-color: transparent;
    color: ${({ mainColor }) => mainColor};
    border: 1px solid ${({ mainColor }) => mainColor};
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background-color: ${({ mainColor }) => mainColor};
        color: white;
    }

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const OptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-of-type {
        border-bottom: none;
    }
`;

export const OptionIconCircle = styled.div<{ mainColor: string }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${({ mainColor }) => `${mainColor}15`};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
        fill: ${({ mainColor }) => mainColor};
    }
`;

export const OptionTitle = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #333;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const DeleteButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #e74c3c;
    transition: background-color 0.2s ease;
    border-radius: 4px;
    flex-shrink: 0;

    &:hover {
        background-color: #fde8e8;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const AddOptionSection = styled.div`
    margin-top: 15px;
    padding: 15px;
    border: 1px dashed #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
`;

export const AddOptionButton = styled.button<{ mainColor: string }>`
    padding: 8px 16px;
    background-color: transparent;
    color: ${({ mainColor }) => mainColor};
    border: 1px solid ${({ mainColor }) => mainColor};
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ mainColor }) => mainColor};
        color: white;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const IconPickerGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
`;

export const IconPickerItem = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 2px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    background-color: ${({ isActive, mainColor }) =>
        isActive ? `${mainColor}15` : "#fff"};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }

    svg {
        width: 20px;
        height: 20px;
        fill: ${({ isActive, mainColor }) => (isActive ? mainColor : "#666")};
    }
`;

export const AddFormRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
`;

export const ValidateButton = styled.button<{ mainColor: string }>`
    padding: 8px 16px;
    background-color: ${({ mainColor }) => mainColor};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const CancelButton = styled.button`
    padding: 8px 16px;
    background-color: transparent;
    color: #666;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #f5f5f5;
    }
`;

export const DisabledOverlay = styled.div`
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 14px;
    background-color: #fafafa;
    border-radius: 8px;
    margin-top: 10px;
`;

// ─── Delete Confirmation Popup ──────────────────────────

export const ConfirmOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const ConfirmModal = styled.div`
    background-color: white;
    border-radius: 12px;
    padding: 24px;
    width: 400px;
    max-width: 90vw;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

export const ConfirmTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
`;

export const ConfirmMessage = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0 0 20px 0;
    line-height: 1.5;
`;

export const ConfirmActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

export const ConfirmCancelBtn = styled.button`
    padding: 8px 20px;
    background-color: transparent;
    color: #666;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #f5f5f5;
    }
`;

export const ConfirmDeleteBtn = styled.button`
    padding: 8px 20px;
    background-color: #e74c3c;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

// ─── Upload Icon Button ─────────────────────────────────

export const UploadIconButton = styled.label<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 2px dashed
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    background-color: ${({ isActive, mainColor }) =>
        isActive ? `${mainColor}15` : "#fff"};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }

    svg {
        width: 20px;
        height: 20px;
        fill: ${({ isActive, mainColor }) => (isActive ? mainColor : "#999")};
    }
`;

export const CustomIconPreview = styled.div<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 2px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    background-color: ${({ isActive, mainColor }) =>
        isActive ? `${mainColor}15` : "#fff"};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
    overflow: hidden;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }

    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }
`;
